import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import clsx from "clsx";
import { alpha, Button, InputBase } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { setSearchResults, setSearchQuery } from "../../../actions";
import { useSearchModal } from "../../../hooks";

const useStyles = makeStyles((theme) => ({
  searchWrap: {
    marginLeft: 10,
    maxWidth: 400,
    position: "relative",
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(["width", "background-color"], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeOut,
    }),
    display: "flex",
    alignItems: "center",
    right: 0,
  },
  searchCollapsed: {
    width: 100,
    backgroundColor: alpha(theme.palette.common.grey, 0.25),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.grey, 0.35),
    },
  },
  searchExpanded: {
    width: 260,
    backgroundColor: alpha(theme.palette.common.grey, 0.35),
  },
  searchIcon: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.palette.component.headerColor,
    minWidth: "unset",
  },
  inputRoot: {
    color: theme.palette.component.headerColor,
    width: "100%",
  },
  inputInput: {
    paddingTop: 11,
    paddingRight: 34,
    paddingBottom: 11,
    paddingLeft: 11,
    transition: theme.transitions.create("width"),
    color: theme.palette.component.headerColor,
    fontSize: 12,
    "&::placeholder": {
      color: theme.palette.common.navbarColor,
      opacity: 1,
    },
  },
  zoomIcon: {
    fontSize: 16,
  },
}));

const HeaderSearch = ({
  searchQuery,
  selectedWorkspace,
  setSearchResults,
  setSearchQuery,
}) => {
  const classes = useStyles();
  const search = useSearchModal();
  const intl = useIntl();

  const [searchText, setSearchText] = useState(searchQuery);
  const [focused, setFocused] = useState(false);

  const siteId =
    selectedWorkspace && selectedWorkspace.id ? selectedWorkspace.id : 0;

  useEffect(() => {
    if (siteId && siteId !== 0) {
      setSearchResults({});
    }
  }, [siteId, setSearchResults]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      initiateSearch();
    }
  };

  const initiateSearch = () => {
    setSearchQuery({ searchQuery: searchText });
    search.open();
  };

  return (
    <div className={classes.searchWrap}>
      <div
        className={clsx(
          classes.search,
          focused || searchText
            ? classes.searchExpanded
            : classes.searchCollapsed
        )}
      >
        <InputBase
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          inputProps={{ "aria-label": "Search" }}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={intl.formatMessage({ id: "nav.search" })}
          value={searchText}
        />
        <Button
          className={classes.searchIcon}
          onMouseDown={(e) => e.preventDefault()}
          onClick={initiateSearch}
          tabIndex={-1}
        >
          <i className={`nc-icon nc-zoom-split ${classes.zoomIcon}`} />
        </Button>
      </div>
    </div>
  );
};

HeaderSearch.propTypes = {
  searchDetails: PropTypes.shape({
    name: PropTypes.string,
    showDetails: PropTypes.bool,
  }).isRequired,
  searchQuery: PropTypes.string.isRequired,
  selectedWorkspace: PropTypes.shape({ id: PropTypes.number }).isRequired,
  setSearchResults: PropTypes.func.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    searchDetails: state.ui.toggleDetails,
    searchQuery: state.ui.toggleDetails.searchQuery,
    selectedWorkspace: state.ui.toggleWorkspaces.workspace,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setSearchResults,
      setSearchQuery,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderSearch);
